import api from '@/api/base';
import type { ApiResponse, AdminMeResponse, UserAdminModel } from '@/api/types';

// API URLs for admin endpoints
const URLS = {
  admins: '/admins',
  adminMe: '/admins/me',
  adminUpdateMe: '/admins/updateMe',
  adminById: (id: string) => `/admins/${id}`,
  adminStatus: (id: string) => `/admins/${id}/status`,
  adminRole: (id: string) => `/admins/${id}/role`,
  signin: '/admins/signin',
} as const;

// Admin CRUD endpoints return the shared { status, data } envelope. Request
// bodies are loosely typed (the backend accepts partial admin bodies).
type AdminPayload = Record<string, unknown>;

// Admin API methods
const adminApi = {
  // Admin signin/login — returns { status, token, data: adminUser }
  signin: (email: string, password: string) => {
    return api.post<ApiResponse<UserAdminModel>>(URLS.signin, {
      email,
      password,
    });
  },

  // Get current admin (me) — BARE { message, users }, not the envelope
  getMe: (token?: string) => {
    return api.get<AdminMeResponse>(URLS.adminMe, { token });
  },

  // Update current admin profile (me)
  updateMe: (profileData: AdminPayload, token?: string) => {
    return api.patch<ApiResponse<UserAdminModel>>(
      URLS.adminUpdateMe,
      profileData,
      { token },
    );
  },

  // Get all admins
  getAllAdmins: (token?: string) => {
    return api.get<ApiResponse<UserAdminModel[]>>(URLS.admins, { token });
  },

  // Get admin by ID
  getAdminById: (id: string, token?: string) => {
    return api.get<ApiResponse<UserAdminModel>>(URLS.adminById(id), { token });
  },

  // Create new admin
  createAdmin: (adminData: AdminPayload, token?: string) => {
    return api.post<ApiResponse<UserAdminModel>>(URLS.admins, adminData, {
      token,
    });
  },

  // Update admin
  updateAdmin: (id: string, adminData: AdminPayload, token?: string) => {
    return api.patch<ApiResponse<UserAdminModel>>(
      URLS.adminById(id),
      adminData,
      { token },
    );
  },

  // Delete admin
  deleteAdmin: (id: string, token?: string) => {
    return api.delete<ApiResponse<null>>(URLS.adminById(id), { token });
  },

  // Update admin status — no backing route on the server; result is opaque
  updateAdminStatus: (id: string, status: unknown, token?: string) => {
    return api.patch<unknown>(URLS.adminStatus(id), { status }, { token });
  },

  // Update admin role — no backing route on the server; result is opaque
  updateAdminRole: (id: string, role: unknown, token?: string) => {
    return api.patch<unknown>(URLS.adminRole(id), { role }, { token });
  },
};

export default adminApi;
